import { useRef } from 'react'

// A WAI-ARIA tab strip: one focus stop in the tab order, arrow keys move
// between tabs and select as they go (automatic activation), Home/End jump to
// the ends. The selected tab is told by aria-selected, which the CSS also
// reads for the visible state — one source for both.
//
// `tabs` is [{ id, label, panelId? }]. When the caller passes `id`, each tab
// element is `${id}-tab-${tab.id}` so the panel can point back at it with
// aria-labelledby; `panelId` fills the tab's aria-controls the other way.
export default function Tabs({ id, label, tabs, selected, onSelect, className = '' }) {
  const refs = useRef([])

  function focusAndSelect(index) {
    const count = tabs.length
    const next = ((index % count) + count) % count
    refs.current[next]?.focus()
    onSelect(tabs[next].id)
  }

  function handleKeyDown(event, index) {
    switch (event.key) {
      case 'ArrowRight':
        focusAndSelect(index + 1)
        break
      case 'ArrowLeft':
        focusAndSelect(index - 1)
        break
      case 'Home':
        focusAndSelect(0)
        break
      case 'End':
        focusAndSelect(tabs.length - 1)
        break
      default:
        return
    }
    event.preventDefault()
  }

  return (
    <div role="tablist" aria-label={label} className={`tabs${className ? ` ${className}` : ''}`}>
      {tabs.map((tab, index) => {
        const isSelected = tab.id === selected
        return (
          <button
            key={tab.id}
            ref={(el) => {
              refs.current[index] = el
            }}
            type="button"
            role="tab"
            id={id ? `${id}-tab-${tab.id}` : undefined}
            aria-selected={isSelected}
            aria-controls={tab.panelId}
            tabIndex={isSelected ? 0 : -1}
            className="tabs__tab"
            onClick={() => onSelect(tab.id)}
            onKeyDown={(event) => handleKeyDown(event, index)}
          >
            {tab.label}
          </button>
        )
      })}
    </div>
  )
}
